import React, { useContext, useEffect } from 'react';
import classNames from 'classnames';
import {
  Button,
  Spinner,
  useAnimatedClass,
  useTranslation,
  Divider,
} from '@testsigma/atoms';
import { DocumentURL, logErrorToSentry } from '@utils/common';
import { SalesforceConnectionsService } from '@modules/salesforce/services';
import { UserPreferenceContext } from '@contexts/user-preference-context';

import {
  MetadataConnectionModalScreens,
  RefreshModalScreens,
  useSFConnectionState,
} from '@contexts/sf-connection-context';

import {
  useCheckMetadataInitialSyncStatus,
  useMetadataRefresh,
} from '@modules/salesforce/metadata-connections/hooks';
import { useGlobalState } from '@providers/global-context-provider';

const MetadataPollingScreen: React.FC = () => {
  const { t } = useTranslation();
  const { userPreference } = useContext(UserPreferenceContext);
  const projectPreference = userPreference?.preference?.projectPreference;

  const {
    handleUpdateMetadataConnectionModalScreen,
    selectedConnection,
    handleUpdateOverlayMode,
    handleMetadataConnection,
    handleUpdateSelectedConnection,
  } = useSFConnectionState();

  const {
    handleCustomObjectSyncing,
    handleStartRefreshingMetadata,
    handleStopRefreshingMetadata,
  } = useGlobalState();

  const { applicationVersionId, applicationId } = selectedConnection;
  const handleElementCreation = async () => {
    if (!applicationVersionId) {
      return;
    }
    try {
      handleUpdateMetadataConnectionModalScreen(
        MetadataConnectionModalScreens.METADATA_SYNC_DATA_PREPARATION_SCREEN,
      );
      await SalesforceConnectionsService.updateVersionId({
        applicationVersionId,
        applicationId: applicationId || projectPreference?.applicationId,
        connection: selectedConnection,
      });
    } catch (error) {
      logErrorToSentry({ error });
    }
  };

  const animationClass = useAnimatedClass('reval-animation-opacity');

  const { handleStartInitialSyncPolling } = useCheckMetadataInitialSyncStatus({
    onSuccess: () => {
      handleElementCreation();
      handleUpdateMetadataConnectionModalScreen(
        MetadataConnectionModalScreens.METADATA_SYNC_SUCCESSFUL_SCREEN,
      );
      handleCustomObjectSyncing(false);
      handleStopRefreshingMetadata(selectedConnection?.id);
    },
    onFailure: () => {
      handleUpdateMetadataConnectionModalScreen(
        MetadataConnectionModalScreens.METADATA_SYNC_FAILURE_SCREEN,
      );
      handleCustomObjectSyncing(false);
      handleStopRefreshingMetadata(selectedConnection?.id);
    },
    onPartiallySuccess: () => {
      handleUpdateOverlayMode('metadata_connection');
      handleUpdateMetadataConnectionModalScreen(
        MetadataConnectionModalScreens.METADATA_SYNC_PARTIALLY_SUCCESSFUL_SCREEN,
      );
      handleMetadataConnection(selectedConnection);
      handleUpdateSelectedConnection(selectedConnection);
      handleCustomObjectSyncing(true);
      handleStartRefreshingMetadata(selectedConnection.id);
    },
  });

  useEffect(() => {
    handleStartInitialSyncPolling();
  }, [handleStartInitialSyncPolling]);

  return (
    <>
      <div className='flex items-center justify-between h-[60px]'>
        <div className='ml-8 items-center my-4 font-medium text-[18px] text-secondary-unique-600'>
          {t('metadata.connection.label')}
        </div>
        <DocumentURL
          path='/docs/salesforce-testing/metadata-connections/'
          label='add-connection.info.link'
          className='r-5 items-center my-4 text-sm text-primary-1000'
        />
      </div>
      <Divider type='solid' />
      <div className='py-8 px-10'>
        <div
          className={classNames(
            'flex flex-col py-2 justify-center items-center h-full',
            animationClass,
          )}
        >
          <span className='w-[140px] h-[140px]'>
            <Spinner
              size={24}
              thickness={50}
              speed={100}
              color='#03A973'
              secondaryColor='rgba(172, 57, 57, 0)'
            />
          </span>
          <span className='mt-3 text-15 font-semibold text-secondary-unique-600'>
            {t('syncing-metadata.status')}
          </span>
          <div className='my-10 text-sm leading-5 text-secondary-unique-600'>
            {t('syncing-metadata-waiting.description')}
          </div>
          <Button
            type='primary'
            onClick={() =>
              window.open(
                'https://s3.amazonaws.com/static-docs.testsigma.com/product-videos/salesforce/Salesforce_Demo.mp4',
                '_blank',
              )
            }
          >
            {t('watch-video.button.label')}
          </Button>
        </div>
      </div>
    </>
  );
};

export { MetadataPollingScreen };
